import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Checkbox,
  Tabs,
  Upload,
  Rate,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom';
import { Alert } from 'flowbite-react';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import {
  dispatchService,
  CreateDispatchData,
  DispatchCheckin,
  DispatchCheckout,
  DispatchCollect,
  DispatchReturn,
  DispatchComment,
  DispatchReview,
  dispatchCommentService,
} from '../services/dispatches';
import { customerService } from '../services/customers';
import { businessService } from '../services/businesses';
import { taskTypeService } from '../services/taskTypes';
import { taskTemplateService } from '../services/taskTemplates';
import { staffService, Staff } from '../services/staffs';
import { Task, taskService } from '../services/tasks';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd';
import api from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

export const DispatchFormPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const isCreateMode = !isEdit;
  const [form] = Form.useForm();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();
  const { user } = useAuth();
  const canDelete = hasPermission(user, 'projects.dispatch.delete');
  const canVerifyDispatch = hasPermission(user, 'projects.dispatch.verify');
  const canViewDetails = hasPermission(user, 'projects.dispatch.details.view');
  const canCreateDetails = hasPermission(user, 'projects.dispatch.details.create');
  const canEditDetails = hasPermission(user, 'projects.dispatch.details.update');
  const canViewCollectReturn = hasPermission(user, 'projects.dispatch.details-collect-return-date.view');
  const canCreateCollectReturn = hasPermission(user, 'projects.dispatch.details-collect-return-date.create');
  const canEditCollectReturn = hasPermission(user, 'projects.dispatch.details-collect-return-date.update');
  const canViewLocation = hasPermission(user, 'projects.dispatch.details-location.view');
  const canCreateLocation = hasPermission(user, 'projects.dispatch.details-location.create');
  const canEditLocation = hasPermission(user, 'projects.dispatch.details-location.update');
  const canViewChecklist = hasPermission(user, 'projects.dispatch.checklist.view');
  const canCreateChecklist = hasPermission(user, 'projects.dispatch.checklist.create');
  const canEditChecklist = hasPermission(user, 'projects.dispatch.checklist.update');
  const canViewPhoto = hasPermission(user, 'projects.dispatch.photo.view');
  const canCreatePhoto = hasPermission(user, 'projects.dispatch.photo.create');
  const canEditPhoto = hasPermission(user, 'projects.dispatch.photo.update');
  const canViewComment = hasPermission(user, 'projects.dispatch.comment.view');
  const canCreateComment = hasPermission(user, 'projects.dispatch.comment.create');
  const canEditComment = hasPermission(user, 'projects.dispatch.comment.update');
  const canViewReview = hasPermission(user, 'projects.dispatch.review.view');
  const canCreateReview = hasPermission(user, 'projects.dispatch.review.create');
  const canEditReview = hasPermission(user, 'projects.dispatch.review.update');
  const canEditAny = [
    canEditDetails,
    canEditCollectReturn,
    canEditLocation,
    canEditChecklist,
    canEditPhoto,
    canEditComment,
    canEditReview,
  ].some(Boolean);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDispatch, setLoadingDispatch] = useState(false);
  const [submitNotice, setSubmitNotice] = useState<{ type: 'success' | 'failure'; message: string } | null>(null);
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'overview');
  const [customers, setCustomers] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [customerType, setCustomerType] = useState<'personal' | 'business'>('personal');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [taskTypes, setTaskTypes] = useState<any[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<any[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<Staff[]>([]);
  const [checkins, setCheckins] = useState<DispatchCheckin[]>([]);
  const [checkouts, setCheckouts] = useState<DispatchCheckout[]>([]);
  const [collects, setCollects] = useState<DispatchCollect[]>([]);
  const [returns, setReturns] = useState<DispatchReturn[]>([]);
  const [collectUploads, setCollectUploads] = useState<Record<string, File | null>>({});
  const [returnUploads, setReturnUploads] = useState<Record<string, File | null>>({});
  const [collectPreviewUrls, setCollectPreviewUrls] = useState<Record<string, string>>({});
  const [returnPreviewUrls, setReturnPreviewUrls] = useState<Record<string, string>>({});
  const collectPreviewRef = useRef<Record<string, string>>({});
  const returnPreviewRef = useRef<Record<string, string>>({});
  const [comments, setComments] = useState<DispatchComment[]>([]);
  const [reviews, setReviews] = useState<DispatchReview[]>([]);
  const [commentText, setCommentText] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState<number | null>(null);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Dispatches
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);
  const [dispatchName, setDispatchName] = useState<string>('');
  const [assignedAgentId, setAssignedAgentId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(!isEdit); // For new dispatches, default to editing mode
  const navigate = useNavigate();

  useEffect(() => {
    fetchInitialData();
    if (isEdit && id) {
      loadDispatch(parseInt(id, 10));
    } else {
      // Set default status for new dispatch
      form.setFieldsValue({ status: 'Plan' });
    }
  }, [isEdit, id]);

  // Sync activeTab with URL parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const allowViewDetails = isCreateMode ? canCreateDetails : canViewDetails;
  const allowEditDetails = isEditing && (isCreateMode ? canCreateDetails : canEditDetails);
  const allowViewCollectReturn = isCreateMode ? canCreateCollectReturn : canViewCollectReturn;
  const allowEditCollectReturn = isEditing && (isCreateMode ? canCreateCollectReturn : canEditCollectReturn);
  const allowViewLocation = isCreateMode ? canCreateLocation : canViewLocation;
  const allowEditLocation = isEditing && (isCreateMode ? canCreateLocation : canEditLocation);
  const allowViewChecklist = isCreateMode ? canCreateChecklist : canViewChecklist;
  const allowEditChecklist = isEditing && (isCreateMode ? canCreateChecklist : canEditChecklist);
  const allowViewPhoto = isCreateMode ? canCreatePhoto : canViewPhoto;
  const allowEditPhoto = isEditing && (isCreateMode ? canCreatePhoto : canEditPhoto);
  const allowViewComment = isCreateMode ? canCreateComment : canViewComment;
  const allowEditComment = isEditing && (isCreateMode ? canCreateComment : canEditComment);
  const allowViewReview = isCreateMode ? canCreateReview : canViewReview;
  const allowEditReview = isEditing && (isCreateMode ? canCreateReview : canEditReview);
  const canEditRecord = Boolean(canEditAny);

  const tabVisibility = [
    { key: 'overview', visible: allowViewDetails },
    { key: 'checklist', visible: allowViewChecklist },
    { key: 'photo', visible: allowViewPhoto },
    { key: 'comment', visible: allowViewComment },
    { key: 'review', visible: allowViewReview },
  ];

  useEffect(() => {
    const current = tabVisibility.find((tab) => tab.key === activeTab);
    if (current?.visible) {
      return;
    }
    const fallback = tabVisibility.find((tab) => tab.visible)?.key || 'overview';
    if (fallback !== activeTab) {
      setActiveTab(fallback);
      setSearchParams({ tab: fallback });
    }
  }, [
    activeTab,
    allowViewDetails,
    allowViewChecklist,
    allowViewPhoto,
    allowViewComment,
    allowViewReview,
    setSearchParams,
  ]);
  useEffect(() => {
    if (activeTab === 'status-logs') {
      setActiveTab('overview');
      setSearchParams({ tab: 'overview' });
    }
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    collectPreviewRef.current = collectPreviewUrls;
  }, [collectPreviewUrls]);

  useEffect(() => {
    returnPreviewRef.current = returnPreviewUrls;
  }, [returnPreviewUrls]);

  // Handle tab change and update URL
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const fetchInitialData = async () => {
    try {
      const [customersResponse, businessesResponse, taskTypesResponse, taskTemplatesResponse, staffResponse, tasksResponse] = await Promise.all([
        customerService.getAll(),
        businessService.getAll().catch(() => ({ data: [] })),
        taskTypeService.getAll(),
        taskTemplateService.getAll(),
        staffService.getAll().catch(() => []),
        taskService.getAll().catch(() => ({ data: [] })),
      ]);
      setCustomers(customersResponse.data || customersResponse || []);
      setBusinesses(businessesResponse.data || []);
      setTaskTypes(taskTypesResponse.data || taskTypesResponse || []);
      setTaskTemplates(taskTemplatesResponse.data || taskTemplatesResponse || []);
      setTasks(tasksResponse.data || []);
      setUsers(Array.isArray(staffResponse) ? staffResponse : []);
    } catch {
      message.error('Failed to load initial data');
    }
  };

  const loadDispatch = async (dispatchId: number) => {
    setLoadingDispatch(true);
    try {
      const dispatch = await dispatchService.getById(dispatchId);
      setAssignedAgentId(dispatch.agent_id ?? null);
      const resolvedType = (dispatch as any).type ?? ((dispatch as any).business_id ? 'business' : 'personal');
      const resolvedReferenceId = (dispatch as any).reference_id ?? ((dispatch as any).business_id ?? dispatch.customer_id);
      
      form.setFieldsValue({
        customer_id: dispatch.customer_id,
        reference_number: dispatch.reference_number ?? undefined,
        task_type_id: dispatch.task_type_id,
        year: dispatch.year,
        task_id: dispatch.task_id ?? dispatch.task?.id ?? undefined,
        task_template_id: dispatch.task_template_id ?? dispatch.task?.task_template_id ?? dispatch.task?.taskTemplate?.id ?? undefined,
        create_date: dispatch.create_date ? dayjs(dispatch.create_date) : null,
        collect_date: dispatch.collect_date ? dayjs(dispatch.collect_date) : null,
        collect_due: dispatch.collect_due ? dayjs(dispatch.collect_due) : null,
        return_date: dispatch.return_date ? dayjs(dispatch.return_date) : null,
        return_due: dispatch.return_due ? dayjs(dispatch.return_due) : null,
        location: dispatch.location,
        agent_id: dispatch.agent_id,
        status: dispatch.status === 'Pending' ? 'Plan' : (dispatch.status || 'Plan'),
        type: resolvedType,
        reference_id: resolvedReferenceId,
      });
      
      setCustomerType(resolvedType);
      // Handle business_id
      if (resolvedType === 'business' && resolvedReferenceId) {
        form.setFieldsValue({ business_id: resolvedReferenceId, reference_id: resolvedReferenceId });
        let business = businesses.find(b => b.id === resolvedReferenceId)
          || businesses.find(b => b.id === (dispatch as any).business_id);
        if (!business) {
          try {
            business = await businessService.getById(resolvedReferenceId);
          } catch {
            business = undefined;
          }
        }
        if (business) {
          setSelectedBusiness(business);
          if (business.customer_id) {
            try {
              const customer = await customerService.getById(business.customer_id);
              setSelectedCustomer(customer);
              form.setFieldsValue({ customer_id: business.customer_id });
            } catch {
              // Ignore error
            }
          }
        }
      } else if (resolvedType === 'personal' && resolvedReferenceId) {
        form.setFieldsValue({ customer_id: resolvedReferenceId, reference_id: resolvedReferenceId });
        try {
          const customer = await customerService.getById(resolvedReferenceId);
          setSelectedCustomer(customer);
        } catch {
          // Ignore error
        }
      }
      
      const normalizedCheckins = (dispatch.checkins || []).map((item: any) => ({
        ...item,
        completed: item.completed ?? false,
      }));
      const normalizedCheckouts = (dispatch.checkouts || []).map((item: any) => ({
        ...item,
        completed: item.completed ?? false,
      }));
      setCheckins(normalizedCheckins);
      setCheckouts(normalizedCheckouts);
      setCollects(dispatch.collects || []);
      setReturns(dispatch.returns || []);
      setCollectUploads({});
      setReturnUploads({});
      setComments(dispatch.comments || []);
      setReviews(dispatch.reviews || []);
      if (dispatch.task) {
        setTasks((prev) => (prev.some((task) => task.id === dispatch.task!.id) ? prev : [...prev, dispatch.task!]));
      }
      console.log('Dispatch edit load task', {
        dispatchId,
        task_id: dispatch.task_id,
        task_template_id: (dispatch as any).task_template_id,
        task: dispatch.task,
      });
      
      // Set dispatch name immediately after loading
      setDispatchName(formatDispatchNumber(dispatchId, dispatch.reference_number));
    } catch {
      message.error('Failed to load dispatch');
    } finally {
      setLoadingDispatch(false);
    }
  };

  const handleCustomerTypeChange = (type: 'personal' | 'business') => {
    setCustomerType(type);
    setSelectedCustomer(null);
    setSelectedBusiness(null);
    form.setFieldsValue({
      type,
      reference_id: undefined,
      customer_id: undefined,
      business_id: undefined,
      agent_id: undefined,
      task_id: undefined,
    });
  };

  const handleBusinessChange = async (businessId: number) => {
    if (businessId) {
      try {
        const business = await businessService.getById(businessId);
        setSelectedBusiness(business);
        const businessAdvisorId =
          (business as any).advisor_staff_id
          || business.advisorStaff?.id
          || business.advisor_staff?.id;
        if (business.customer_id) {
          const customer = await customerService.getById(business.customer_id);
          setSelectedCustomer(customer);
          const customerAdvisorId =
            (customer as any).agent_staff_id
            || customer.advisorStaff?.id
            || customer.advisor_staff?.id;
          form.setFieldsValue({
            customer_id: business.customer_id,
            reference_id: businessId,
            agent_id: businessAdvisorId || customerAdvisorId || undefined,
          });
        } else {
          form.setFieldsValue({
            customer_id: undefined,
            reference_id: businessId,
            agent_id: businessAdvisorId || undefined,
          });
        }
      } catch {
        message.error('Failed to load business details');
      }
    } else {
      setSelectedBusiness(null);
      setSelectedCustomer(null);
      form.setFieldsValue({
        customer_id: undefined,
        reference_id: undefined,
        agent_id: undefined,
      });
    }
  };

  const handleCustomerChange = async (customerId: number) => {
    if (customerId) {
      try {
        const customer = await customerService.getById(customerId);
        setSelectedCustomer(customer);
        const customerAdvisorId =
          (customer as any).agent_staff_id
          || customer.advisorStaff?.id
          || customer.advisor_staff?.id;
        form.setFieldsValue({ agent_id: customerAdvisorId, reference_id: customerId });
      } catch {
        message.error('Failed to load customer');
      }
    } else {
      setSelectedCustomer(null);
      form.setFieldsValue({ agent_id: null, reference_id: undefined });
    }
  };

  const formatTaskNumber = (taskId: number, referenceNumber?: string) => {
    return referenceNumber || `#${taskId.toString().padStart(7, '0')}`;
  };

  const getAvailableTasks = () => {
    const customerId = form.getFieldValue('customer_id');
    const businessId = form.getFieldValue('business_id');
    if (businessId) {
      return tasks.filter((task) => task.business_id === businessId);
    }
    if (customerId) {
      return tasks.filter((task) => task.customer_id === customerId);
    }
    return tasks;
  };

  const handleTaskChange = (taskId?: number) => {
    if (!taskId) {
      form.setFieldsValue({ task_id: undefined, task_template_id: undefined });
      return;
    }
    const selectedTask = tasks.find((task) => task.id === taskId);
    const selectedTemplateId = selectedTask?.task_template_id ?? selectedTask?.taskTemplate?.id;
    form.setFieldsValue({
      task_id: taskId,
      task_type_id: selectedTask?.task_type_id ?? form.getFieldValue('task_type_id'),
      year: selectedTask?.year ?? form.getFieldValue('year'),
      task_template_id: selectedTemplateId ?? form.getFieldValue('task_template_id'),
    });
    if (selectedTemplateId) {
      handleTaskTemplateChange(selectedTemplateId);
    }
  };

  const getCollectKey = (collect: DispatchCollect, index: number) => {
    return String(collect.id ?? `collect-${index}`);
  };

  const getReturnKey = (returnItem: DispatchReturn, index: number) => {
    return String(returnItem.id ?? `return-${index}`);
  };

  const getAttachmentName = (path?: string | null) => {
    if (!path) return 'attachment';
    const parts = path.split('/');
    return parts[parts.length - 1] || 'attachment';
  };

  useEffect(() => {
    let isCancelled = false;
    const activeKeys = new Set(collects.map((collect, index) => getCollectKey(collect, index)));

    setCollectPreviewUrls((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (!activeKeys.has(key)) {
          URL.revokeObjectURL(next[key]);
          delete next[key];
        }
      });
      return next;
    });

    const loadPreview = async (collect: DispatchCollect, index: number) => {
      const key = getCollectKey(collect, index);
      const file = collectUploads[key];

      if (file instanceof File) {
        const blobUrl = URL.createObjectURL(file);
        if (isCancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        setCollectPreviewUrls((prev) => {
          if (prev[key] && prev[key] !== blobUrl) {
            URL.revokeObjectURL(prev[key]);
          }
          return { ...prev, [key]: blobUrl };
        });
        return;
      }

      if (!collect.attachment || !isEdit || !id || typeof collect.id !== 'number') {
        setCollectPreviewUrls((prev) => {
          if (!prev[key]) return prev;
          URL.revokeObjectURL(prev[key]);
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }

      if (collectPreviewRef.current[key]) {
        return;
      }

      try {
        const url = `/dispatches/${id}/collects/${collect.id}/attachment/view`;
        const response = await api.get(url, { responseType: 'blob' });
        if (isCancelled) return;
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'image/jpeg',
        });
        const blobUrl = URL.createObjectURL(blob);
        setCollectPreviewUrls((prev) => {
          if (prev[key] && prev[key] !== blobUrl) {
            URL.revokeObjectURL(prev[key]);
          }
          return { ...prev, [key]: blobUrl };
        });
      } catch {
        // Ignore preview errors; view/download still works.
      }
    };

    collects.forEach((collect, index) => {
      loadPreview(collect, index);
    });

    return () => {
      isCancelled = true;
    };
  }, [collects, collectUploads, id, isEdit]);

  useEffect(() => {
    let isCancelled = false;
    const activeKeys = new Set(returns.map((item, index) => getReturnKey(item, index)));

    setReturnPreviewUrls((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (!activeKeys.has(key)) {
          URL.revokeObjectURL(next[key]);
          delete next[key];
        }
      });
      return next;
    });

    const loadPreview = async (returnItem: DispatchReturn, index: number) => {
      const key = getReturnKey(returnItem, index);
      const file = returnUploads[key];

      if (file instanceof File) {
        const blobUrl = URL.createObjectURL(file);
        if (isCancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        setReturnPreviewUrls((prev) => {
          if (prev[key] && prev[key] !== blobUrl) {
            URL.revokeObjectURL(prev[key]);
          }
          return { ...prev, [key]: blobUrl };
        });
        return;
      }

      if (!returnItem.attachment || !isEdit || !id || typeof returnItem.id !== 'number') {
        setReturnPreviewUrls((prev) => {
          if (!prev[key]) return prev;
          URL.revokeObjectURL(prev[key]);
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }

      if (returnPreviewRef.current[key]) {
        return;
      }

      try {
        const url = `/dispatches/${id}/returns/${returnItem.id}/attachment/view`;
        const response = await api.get(url, { responseType: 'blob' });
        if (isCancelled) return;
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'image/jpeg',
        });
        const blobUrl = URL.createObjectURL(blob);
        setReturnPreviewUrls((prev) => {
          if (prev[key] && prev[key] !== blobUrl) {
            URL.revokeObjectURL(prev[key]);
          }
          return { ...prev, [key]: blobUrl };
        });
      } catch {
        // Ignore preview errors; view/download still works.
      }
    };

    returns.forEach((item, index) => {
      loadPreview(item, index);
    });

    return () => {
      isCancelled = true;
    };
  }, [returns, returnUploads, id, isEdit]);

  const handleDownloadAttachment = async (
    type: 'collect' | 'return',
    itemId?: number,
    file?: UploadFile
  ) => {
    try {
      if (file?.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.name || 'attachment';
        link.click();
        URL.revokeObjectURL(blobUrl);
        return;
      }

      if (!id || !itemId) return;
      const url =
        type === 'collect'
          ? `/dispatches/${id}/collects/${itemId}/attachment/download`
          : `/dispatches/${id}/returns/${itemId}/attachment/download`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'attachment';
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      message.error('Failed to download attachment');
    }
  };

  const handleTaskTemplateChange = async (templateId?: number) => {
    if (!templateId) {
      setCheckins([]);
      setCheckouts([]);
      form.setFieldsValue({ task_template_id: undefined });
      return;
    }
    form.setFieldsValue({ task_template_id: templateId });
    try {
      const response = await api.get('/sub-tasks');
      const allSubTasks = response.data?.data || response.data || [];
      const subTasks = allSubTasks.filter((st: any) => st.task_template_id === templateId);
      const checkinTemplates = subTasks.filter((st: any) => st.list_type === 'check_in');
      const checkoutTemplates = subTasks.filter((st: any) => st.list_type === 'check_out');
      const generalTemplates = subTasks.filter((st: any) => !st.list_type || st.list_type === 'general');
      const useFallback = checkinTemplates.length === 0 && checkoutTemplates.length === 0;
      const resolvedCheckins = useFallback ? generalTemplates : checkinTemplates;
      const resolvedCheckouts = useFallback ? generalTemplates : checkoutTemplates;
      const seed = Date.now();
      const newCheckins = resolvedCheckins.map((st: any, index: number) => ({
        id: `temp-checkin-${seed}-${index}`,
        dispatch_id: id ? parseInt(id, 10) : 0,
        name: st.name || '',
        completed: false,
        created_at: new Date().toISOString(),
      }));
      const newCheckouts = resolvedCheckouts.map((st: any, index: number) => ({
        id: `temp-checkout-${seed}-${index}`,
        dispatch_id: id ? parseInt(id, 10) : 0,
        name: st.name || '',
        completed: false,
        created_at: new Date().toISOString(),
      }));
      setCheckins(newCheckins);
      setCheckouts(newCheckouts);
    } catch {
      message.error('Failed to load checklist');
    }
  };

  const handleAddCheckin = () => {
    const newCheckin: DispatchCheckin = {
      id: `temp-checkin-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      name: '',
      completed: false,
      created_at: new Date().toISOString(),
    };
    setCheckins(prev => [...prev, newCheckin]);
  };

  const handleDeleteCheckin = (checkinId: number | string) => {
    setCheckins(prev => prev.filter(c => c.id !== checkinId));
  };

  const handleAddCheckout = () => {
    const newCheckout: DispatchCheckout = {
      id: `temp-checkout-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      name: '',
      completed: false,
      created_at: new Date().toISOString(),
    };
    setCheckouts(prev => [...prev, newCheckout]);
  };

  const handleDeleteCheckout = (checkoutId: number | string) => {
    setCheckouts(prev => prev.filter(c => c.id !== checkoutId));
  };

  const handleAddCollect = () => {
    const newCollect: DispatchCollect = {
      id: `temp-collect-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      name: '',
      attachment: null,
      enabled: true,
      created_at: new Date().toISOString(),
    };
    setCollects(prev => [...prev, newCollect]);
  };

  const handleDeleteCollect = (collectId: number | string) => {
    setCollects(prev => prev.filter(c => c.id !== collectId));
    setCollectUploads(prev => {
      const next = { ...prev };
      delete next[String(collectId)];
      return next;
    });
  };

  const handleAddReturn = () => {
    const newReturn: DispatchReturn = {
      id: `temp-return-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      name: '',
      attachment: null,
      enabled: true,
      created_at: new Date().toISOString(),
    };
    setReturns(prev => [...prev, newReturn]);
  };

  const handleDeleteReturn = (returnId: number | string) => {
    setReturns(prev => prev.filter(r => r.id !== returnId));
    setReturnUploads(prev => {
      const next = { ...prev };
      delete next[String(returnId)];
      return next;
    });
  };

  const handleAddComment = () => {
    if (!allowEditComment) {
      message.warning('You do not have permission to add comments');
      return;
    }
    if (!commentText.trim()) {
      message.error('Please enter a comment');
      return;
    }
    const newComment: DispatchComment = {
      id: `temp-comment-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      comment: commentText,
      created_at: new Date().toISOString(),
      user: { id: user?.id || 0, name: user?.name || 'Current User', email: user?.email || '' },
    };
    setComments(prev => [...prev, newComment]);
    setCommentText('');
    message.success('Comment added (will be saved on submit)');
  };

  const handleDeleteComment = async (commentId: number | string) => {
    if (!canDelete) {
      message.warning('You do not have permission to delete comments');
      return;
    }
    if (typeof commentId === 'number') {
      try {
        await dispatchCommentService.delete(commentId);
        setComments(prev => prev.filter(c => String(c.id) !== String(commentId)));
        message.success('Comment deleted');
        return;
      } catch {
        message.error('Failed to delete comment');
        if (id) {
          loadDispatch(parseInt(id, 10));
        }
        return;
      }
    }
    setComments(prev => prev.filter(c => String(c.id) !== String(commentId)));
  };

  const handleAddReview = () => {
    if (!allowEditReview) {
      message.warning('You do not have permission to add reviews');
      return;
    }
    if (!reviewText.trim()) {
      message.error('Please enter a review');
      return;
    }
    if (!reviewRating) {
      message.error('Please select a rating');
      return;
    }
    const newReview: DispatchReview = {
      id: `temp-review-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      customer_id: undefined,
      review: reviewText,
      rating: reviewRating,
      created_at: new Date().toISOString(),
      creator: { id: user?.id || 0, name: user?.name || 'Current User', email: user?.email || '' },
    };
    setReviews(prev => [...prev, newReview]);
    setReviewText('');
    setReviewRating(null);
    message.success('Review added (will be saved on submit)');
  };

  const handleDeleteReview = (reviewId: number | string) => {
    if (!canDelete) {
      message.warning('You do not have permission to delete reviews');
      return;
    }
    setReviews(prev => prev.filter(r => r.id !== reviewId));
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${day}/${month}/${year} ${displayHours}:${minutes}${ampm}`;
  };

  const getDisplayValue = (fieldName: string, type: 'select' | 'date' | 'number' | 'text' = 'text', options?: any[]) => {
    const value = form.getFieldValue(fieldName);
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    if (type === 'select' && options) {
      const option = options.find(opt => opt.id === value);
      return option?.name || '-';
    }
    
    if (type === 'date') {
      if (!value) return '-';
      if (dayjs.isDayjs(value)) {
        return value.format('DD/MM/YYYY');
      }
      if (typeof value === 'string') {
        return dayjs(value).format('DD/MM/YYYY');
      }
      return '-';
    }
    
    if (type === 'number') {
      return value || '-';
    }
    
    return value || '-';
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      // Remove fields that don't exist in dispatches table
      const { sender_id, team_id, staff_id, ...cleanValues } = values;

      const checkinPayload = checkins.map(c => ({
        id: typeof c.id === 'number' ? c.id : undefined,
        name: c.name,
        completed: c.completed ?? false,
      }));
      const checkoutPayload = checkouts.map(c => ({
        id: typeof c.id === 'number' ? c.id : undefined,
        name: c.name,
        completed: c.completed ?? false,
      }));
      const collectPayload = collects.map((c, index) => {
        const key = getCollectKey(c, index);
        const file = collectUploads[key];
        return {
          id: typeof c.id === 'number' ? c.id : undefined,
          name: c.name || file?.name || '',
          enabled: c.enabled,
          attachment: file ? undefined : c.attachment,
        };
      });
      const returnPayload = returns.map((r, index) => {
        const key = getReturnKey(r, index);
        const file = returnUploads[key];
        return {
          id: typeof r.id === 'number' ? r.id : undefined,
          name: r.name || file?.name || '',
          enabled: r.enabled,
          attachment: file ? undefined : r.attachment,
        };
      });
      const commentPayload = comments
        .filter(c => typeof c.id === 'string' && String(c.id).startsWith('temp-'))
        .map(c => ({ comment: c.comment }));
      const reviewPayload = reviews
        .filter(r => typeof r.id === 'string' && String(r.id).startsWith('temp-'))
        .map(r => ({ customer_id: r.customer_id, review: r.review, rating: r.rating ?? null }));

      const referenceId = customerType === 'business' ? values.business_id : values.customer_id;
      const rawStatus = values.status || form.getFieldValue('status') || 'Plan';
      const normalizedStatus = rawStatus === 'Pending' ? 'Plan' : rawStatus;
      const payload: CreateDispatchData = {
        ...cleanValues,
        task_id: values.task_id,
        type: customerType,
        reference_id: referenceId,
        customer_id: values.business_id ? selectedBusiness?.customer_id : values.customer_id,
        business_id: values.business_id || undefined,
        status: normalizedStatus,
        create_date: values.create_date?.format('YYYY-MM-DD'),
        collect_date: values.collect_date?.format('YYYY-MM-DD'),
        collect_due: values.collect_due?.format('YYYY-MM-DD'),
        return_date: values.return_date?.format('YYYY-MM-DD'),
        return_due: values.return_due?.format('YYYY-MM-DD'),
        checkins: checkinPayload,
        checkouts: checkoutPayload,
        collects: collectPayload,
        returns: returnPayload,
        comments: commentPayload,
        reviews: reviewPayload,
      };

      const hasCollectFiles = Object.values(collectUploads).some(file => file instanceof File);
      const hasReturnFiles = Object.values(returnUploads).some(file => file instanceof File);
      const hasFiles = hasCollectFiles || hasReturnFiles;
      let submitData: CreateDispatchData | FormData = payload;

      if (hasFiles) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return;
          }
          if (Array.isArray(value)) {
            return;
          }
          formData.append(key, value.toString());
        });

        checkinPayload.forEach((item, index) => {
          Object.entries(item).forEach(([itemKey, itemValue]) => {
            if (itemValue !== undefined && itemValue !== null) {
              formData.append(`checkins[${index}][${itemKey}]`, itemValue.toString());
            }
          });
        });
        checkoutPayload.forEach((item, index) => {
          Object.entries(item).forEach(([itemKey, itemValue]) => {
            if (itemValue !== undefined && itemValue !== null) {
              formData.append(`checkouts[${index}][${itemKey}]`, itemValue.toString());
            }
          });
        });
        collectPayload.forEach((item, index) => {
          Object.entries(item).forEach(([itemKey, itemValue]) => {
            if (itemValue !== undefined && itemValue !== null && itemKey !== 'attachment') {
              formData.append(`collects[${index}][${itemKey}]`, itemValue.toString());
            }
          });
          const key = getCollectKey(collects[index], index);
          const file = collectUploads[key];
          if (file) {
            formData.append(`collects[${index}][attachment]`, file);
          } else if (item.attachment !== undefined) {
            formData.append(`collects[${index}][attachment]`, item.attachment ?? '');
          }
        });
        returnPayload.forEach((item, index) => {
          Object.entries(item).forEach(([itemKey, itemValue]) => {
            if (itemValue !== undefined && itemValue !== null && itemKey !== 'attachment') {
              formData.append(`returns[${index}][${itemKey}]`, itemValue.toString());
            }
          });
          const key = getReturnKey(returns[index], index);
          const file = returnUploads[key];
          if (file) {
            formData.append(`returns[${index}][attachment]`, file);
          } else if (item.attachment !== undefined) {
            formData.append(`returns[${index}][attachment]`, item.attachment ?? '');
          }
        });
        commentPayload.forEach((item, index) => {
          formData.append(`comments[${index}][comment]`, item.comment);
        });
        reviewPayload.forEach((item, index) => {
          if (item.customer_id !== undefined && item.customer_id !== null) {
            formData.append(`reviews[${index}][customer_id]`, item.customer_id.toString());
          }
          formData.append(`reviews[${index}][review]`, item.review);
          if (item.rating !== undefined && item.rating !== null) {
            formData.append(`reviews[${index}][rating]`, item.rating.toString());
          }
        });

        submitData = formData;
      }

      if (isEdit && id) {
        await dispatchService.update(parseInt(id, 10), submitData);
        setSubmitNotice({
          type: 'success',
          message: 'Dispatch updated successfully.',
        });
        setIsEditing(false);
        loadDispatch(parseInt(id, 10));
      } else {
        const created = await dispatchService.create(submitData);
        const createdId = created?.id;
        const notice = {
          type: 'success' as const,
          message: 'Dispatch created successfully.',
        };
        if (createdId) {
          navigate(`/dispatches/${createdId}/edit`, { state: { notice } });
        } else {
          navigate('/dispatches', { state: { notice } });
        }
      }
    } catch (error: any) {
      console.error('Failed to save dispatch:', error);
      const fallbackMessage = isEdit ? 'Failed to update dispatch.' : 'Failed to create dispatch.';
      const apiMessage = error?.response?.data?.message || error?.message;
      setSubmitNotice({
        type: 'failure',
        message: apiMessage ? `${fallbackMessage} ${apiMessage}` : fallbackMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDispatchNumber = (dispatchId: number, referenceNumber?: string) => {
    return referenceNumber || `#${dispatchId.toString().padStart(7, '0')}`;
  };

  const getDispatchDisplayName = () => {
    if (isEdit && id) {
      return formatDispatchNumber(parseInt(id, 10), form.getFieldValue('reference_number'));
    }
    return 'New Dispatch';
  };

  useEffect(() => {
    if (isEdit && id && customers.length > 0 && taskTypes.length > 0) {
      const displayName = getDispatchDisplayName();
      setDispatchName(displayName);
    }
  }, [isEdit, id, customers, taskTypes, form.getFieldValue('customer_id'), form.getFieldValue('task_type_id')]);

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this dispatch?')) return;
    
    try {
      await dispatchService.delete(parseInt(id, 10));
                      message.success('Dispatch deleted');
                      navigate('/dispatches');
    } catch {
      message.error('Failed to delete dispatch');
                  }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
      {/* Header */}
      <div style={{
        marginBottom: 24,
        paddingBottom: 16,
        borderBottom: '1px solid #f0f0f0',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {isEdit ? (dispatchName || 'Loading...') : 'New Dispatch'}
          </Title>
          <Space>
            {isEdit && !isEditing && canEditAny && canEditRecord && (
              <Button onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
            {isEdit && isEditing && canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
        </div>
      </div>

      {submitNotice && (
        <div style={{ marginBottom: 16 }}>
          <Alert color={submitNotice.type} onDismiss={() => setSubmitNotice(null)}>
            {submitNotice.message}
          </Alert>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={(errorInfo) => {
          const errorMessages = errorInfo.errorFields
            .flatMap((field) => field.errors)
            .filter(Boolean);
          setSubmitNotice({
            type: 'failure',
            message: errorMessages.length > 0 ? errorMessages.join(' ') : 'Please fix the form errors before submitting.',
          });
        }}
        disabled={loadingDispatch || (isEdit && !isEditing)}
        onValuesChange={(changedValues) => {
          if (changedValues.customer_id !== undefined || changedValues.task_type_id !== undefined) {
            const displayName = getDispatchDisplayName();
            setDispatchName(displayName);
          }
        }}
      >
        <Form.Item name="type" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="reference_id" hidden>
          <Input />
        </Form.Item>
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={{
            right: (
              <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status} noStyle>
                {({ getFieldValue }) => {
                  const currentStatus = getFieldValue('status') || 'Plan';
                  const mapStatus = (status: string | undefined): 'Plan' | 'Collect' | 'Return' => {
                    if (!status) return 'Plan';
                    const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
                    if (normalized === 'Pending') {
                      return 'Plan';
                    }
                    if (['Plan', 'Collect', 'Return'].includes(normalized)) {
                      return normalized as 'Plan' | 'Collect' | 'Return';
                    }
                    return 'Plan';
                  };
                  const mappedStatus = mapStatus(currentStatus);
                  
                  const statusConfig = {
                    Plan: { bg: '#E6F7FF', border: '#91D5FF', color: '#1890ff', dot: '#1890ff', label: 'Plan' },
                    Collect: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A', label: 'Collect' },
                    Return: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00', label: 'Return' },
                  };
                  
                  const statuses: Array<'Plan' | 'Collect' | 'Return'> = ['Plan', 'Collect', 'Return'];
                  
                  return (
                    <Space size={8}>
                      {statuses.map((status) => {
                        const isActive = mappedStatus === status;
                        const config = statusConfig[status];
                        return (
                          <div
                            key={status}
                            onClick={() => {
                              if (isEditing) {
                                form.setFieldsValue({ status });
                              }
                            }}
                            style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              backgroundColor: isActive ? config.bg : '#f5f5f5',
                              border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                              color: isActive ? config.color : '#595959',
                              fontWeight: isActive ? 500 : 400,
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              transition: 'all 0.2s',
                              cursor: isEditing ? 'pointer' : 'default',
                            }}
                          >
                            <span style={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              backgroundColor: isActive ? config.dot : '#8c8c8c',
                            }}></span>
                            {config.label}
                          </div>
                        );
                      })}
                    </Space>
                  );
                }}
              </Form.Item>
            ),
          }}
        >
          {/* Overview Tab */}
          {tabVisibility.find((tab) => tab.key === 'overview')?.visible && (
          <TabPane tab="Overview" key="overview">
            <Card style={{ marginBottom: 8 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>ID</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Form.Item name="reference_number" style={{ margin: 0 }}>
                          <Input placeholder="Enter Reference No." />
                        </Form.Item>
                      ) : (
                        <div>{form.getFieldValue('reference_number') || '-'}</div>
                      )}
                    </td>
                  </tr>
                  {/* Customer Type - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Customer</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Space>
                          <Button
                            type={customerType === 'personal' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('personal')}
                            size="small"
                          >
                            Personal
                          </Button>
                          <Button
                            type={customerType === 'business' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('business')}
                            size="small"
                          >
                            Business
                          </Button>
                        </Space>
                      ) : (
                        <div>
                          {customerType === 'personal' ? 'Personal' : 'Business'}
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Customer/Business - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>{customerType === 'personal' ? 'Customer' : 'Business'}</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        customerType === 'personal' ? (
                          <Form.Item
                            name="customer_id"
                            rules={[{ required: true, message: 'Please select customer' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Customer"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleCustomerChange}
                            >
                              {customers.map((customer) => (
                                <Select.Option key={customer.id} value={customer.id}>
                                  {customer.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name="business_id"
                            rules={[{ required: true, message: 'Please select business' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Business"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleBusinessChange}
                            >
                              {businesses.map((business) => (
                                <Select.Option key={business.id} value={business.id}>
                                  {business.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )
                      ) : (
                        <div>
                          {customerType === 'personal' 
                            ? getDisplayValue('customer_id', 'select', customers)
                            : getDisplayValue('business_id', 'select', businesses)
                          }
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Task No. - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Task No.</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Form.Item name="task_id" style={{ margin: 0 }}>
                          <Select
                            placeholder="Select Task"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            onChange={(value) => handleTaskChange(value as number | undefined)}
                          >
                            {getAvailableTasks().map((task) => {
                              const label = formatTaskNumber(task.id, task.reference_number);
                              const templateLabel = task.taskTemplate?.name ? ` - ${task.taskTemplate.name}` : '';
                              return (
                                <Select.Option key={task.id} value={task.id}>
                                  {`${label}${templateLabel}`}
                                </Select.Option>
                              );
                            })}
                          </Select>
                        </Form.Item>
                      ) : (
                        <div>
                          <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue }) => {
                              const taskId = getFieldValue('task_id');
                              if (!taskId) {
                                return '-';
                              }
                              const task = tasks.find((item) => item.id === taskId);
                              const label = formatTaskNumber(taskId, task?.reference_number);
                              return (
                                <Button
                                  type="link"
                                  disabled={false}
                                  onClick={() => navigate(`/tasks/${taskId}/edit`)}
                                  style={{ padding: 0, height: 'auto' }}
                                >
                                  {label}
                                </Button>
                              );
                            }}
                          </Form.Item>
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Task - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Task</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      <div>
                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            return getDisplayValue('task_template_id', 'select', taskTemplates);
                          }}
                        </Form.Item>
                      </div>
                    </td>
                  </tr>
                  {/* Year - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Period</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Form.Item
                          name="year"
                          style={{ margin: 0 }}
                          rules={[
                            {
                              pattern: /^(0[1-9]|1[0-2])\/\d{4}$/,
                              message: 'Use MM/YYYY',
                            },
                          ]}
                          getValueFromEvent={(_date, dateString) => dateString || undefined}
                          getValueProps={(value) => ({
                            value: value ? dayjs(value, 'MM/YYYY') : null,
                          })}
                        >
                          <DatePicker picker="month" format="MM/YYYY" style={{ width: '100%' }} placeholder="MM/YYYY" />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('year', 'text')}
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Create - 单独一行，合并后三列 */}
                  {allowViewDetails && (
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Create</strong>
                      </td>
                      <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                        {allowEditDetails ? (
                          <Form.Item name="create_date" style={{ margin: 0 }}>
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Create Date" />
                          </Form.Item>
                        ) : (
                          <div>
                            {getDisplayValue('create_date', 'date')}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  {allowViewCollectReturn && (
                    <>
                      {/* Collect Date 和 Collect Due - 同一行，各占两列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Collect Date</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          {allowEditCollectReturn ? (
                            <Form.Item name="collect_date" style={{ margin: 0 }}>
                              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Collect Date" />
                            </Form.Item>
                          ) : (
                            <div>
                              {getDisplayValue('collect_date', 'date')}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Collect Due</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          {allowEditCollectReturn ? (
                            <Form.Item name="collect_due" style={{ margin: 0 }}>
                              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Collect Due Date" />
                            </Form.Item>
                          ) : (
                            <div>
                              {getDisplayValue('collect_due', 'date')}
                            </div>
                          )}
                        </td>
                      </tr>
                      {/* Return Date 和 Return Due - 同一行，各占两列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Return Date</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          {allowEditCollectReturn ? (
                            <Form.Item name="return_date" style={{ margin: 0 }}>
                              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Return Date" />
                            </Form.Item>
                          ) : (
                            <div>
                              {getDisplayValue('return_date', 'date')}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Return Due</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          {allowEditCollectReturn ? (
                            <Form.Item name="return_due" style={{ margin: 0 }}>
                              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Return Due Date" />
                            </Form.Item>
                          ) : (
                            <div>
                              {getDisplayValue('return_due', 'date')}
                            </div>
                          )}
                        </td>
                      </tr>
                    </>
                  )}
                  {allowViewLocation && (
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Location</strong>
                      </td>
                      <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                        {allowEditLocation ? (
                          <Form.Item name="location" style={{ margin: 0 }}>
                            <Input placeholder="Enter Location" />
                          </Form.Item>
                        ) : (
                          <div>
                            {getDisplayValue('location', 'text')}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  {!isEditing && allowViewDetails && (
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Advisor</strong>
                      </td>
                      <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                        <div>
                          {selectedBusiness?.advisorStaff?.name
                            || selectedBusiness?.advisor_staff?.name
                            || selectedCustomer?.advisorStaff?.name
                            || selectedCustomer?.advisor_staff?.name
                            || getDisplayValue('agent_id', 'select', users)}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card>
          </TabPane>
          )}

          {/* Checklist Tab */}
          {tabVisibility.find((tab) => tab.key === 'checklist')?.visible && (
          <TabPane tab="Checklist" key="checklist">
            <Row gutter={24}>
              <Col span={12}>
                {/* Check In List */}
            <Card
                  title="Check In List"
                  bodyStyle={{ padding: '16px' }}
              extra={
                allowEditChecklist ? (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={handleAddCheckin}
                  >
                    Add
                  </Button>
                ) : null
              }
            >
                  {checkins.map((checkin, index) => (
                    <div key={checkin.id || index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Checkbox
                        checked={checkin.completed ?? false}
                        disabled={!allowEditChecklist}
                        onChange={(e) => {
                          if (!allowEditChecklist) return;
                          setCheckins(prev => prev.map(c => c.id === checkin.id ? { ...c, completed: e.target.checked } : c));
                        }}
                      />
                  <Input
                        value={checkin.name || ''}
                    disabled={!allowEditChecklist}
                    onChange={(e) => {
                          if (!allowEditChecklist) return;
                          setCheckins(prev => prev.map(c => c.id === checkin.id ? { ...c, name: e.target.value } : c));
                    }}
                    style={{ flex: 1 }}
                    placeholder="Enter name"
                  />
                  <span style={{ color: '#999', marginRight: 8 }}>By</span>
                      {allowEditChecklist && canDelete && (
                        <Button
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteCheckin(checkin.id!)}
                        />
                      )}
                            </div>
                          ))}
                  {checkins.length === 0 && (
                            <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No check ins. Click 'Add' to add a check in.
                            </div>
                          )}
            </Card>
              </Col>
              <Col span={12}>
                {/* Check Out List */}
            <Card
                  title="Check Out List"
                  bodyStyle={{ padding: '16px' }}
              extra={
                allowEditChecklist ? (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={handleAddCheckout}
                  >
                    Add
                  </Button>
                ) : null
              }
            >
                  {checkouts.map((checkout, index) => (
                    <div key={checkout.id || index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Checkbox
                        checked={checkout.completed ?? false}
                        disabled={!allowEditChecklist}
                        onChange={(e) => {
                          if (!allowEditChecklist) return;
                          setCheckouts(prev => prev.map(c => c.id === checkout.id ? { ...c, completed: e.target.checked } : c));
                        }}
                      />
                  <Input
                        value={checkout.name || ''}
                    disabled={!allowEditChecklist}
                    onChange={(e) => {
                          if (!allowEditChecklist) return;
                          setCheckouts(prev => prev.map(c => c.id === checkout.id ? { ...c, name: e.target.value } : c));
                    }}
                    style={{ flex: 1 }}
                    placeholder="Enter name"
                  />
                  <span style={{ color: '#999', marginRight: 8 }}>By</span>
                      {allowEditChecklist && canDelete && (
                        <Button
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteCheckout(checkout.id!)}
                        />
                      )}
                            </div>
                          ))}
                  {checkouts.length === 0 && (
                            <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No check outs. Click 'Add' to add a check out.
                            </div>
                          )}
            </Card>
          </Col>
        </Row>
          </TabPane>
          )}

          {/* Photo Tab */}
          {tabVisibility.find((tab) => tab.key === 'photo')?.visible && (
          <TabPane tab="Photo" key="photo">
            <Row gutter={24}>
              {(isEditing || !isEdit) && (
                <>
                  <Col span={12}>
                    {/* Collect */}
                    <Card
                      title="Collect"
                      bodyStyle={{ padding: '16px' }}
                      extra={
                        allowEditPhoto ? (
                          <Button
                            type="link"
                            icon={<PlusOutlined />}
                            onClick={handleAddCollect}
                          >
                            Add
                          </Button>
                        ) : null
                      }
                    >
                      {collects.map((collect, index) => {
                        const key = getCollectKey(collect, index);
                        const file = collectUploads[key];
                        const existingAttachment = collect.attachment;
                        const previewUrl = collectPreviewUrls[key];
                        const fileList: UploadFile[] = file
                          ? [{
                              uid: `${key}-upload`,
                              name: file.name,
                              status: 'done',
                              originFileObj: file as any,
                            }]
                          : existingAttachment
                            ? [{
                                uid: `${key}-existing`,
                                name: getAttachmentName(existingAttachment),
                                status: 'done',
                              }]
                            : [];

                        return (
                          <div key={collect.id || index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Upload
                              beforeUpload={() => false}
                              maxCount={1}
                              fileList={fileList}
                              disabled={!allowEditPhoto}
                              showUploadList={false}
                              onChange={({ fileList: nextList }) => {
                                if (!allowEditPhoto) return;
                                const nextFile = nextList[0]?.originFileObj as File | undefined;
                                setCollectUploads(prev => ({
                                  ...prev,
                                  [key]: nextFile || null,
                                }));
                                if (nextFile) {
                                  setCollects(prev => prev.map(c => c.id === collect.id ? { ...c, attachment: null, name: c.name || nextFile.name } : c));
                                }
                              }}
                              onRemove={() => {
                                if (!allowEditPhoto) return;
                                setCollectUploads(prev => ({ ...prev, [key]: null }));
                                setCollects(prev => prev.map(c => c.id === collect.id ? { ...c, attachment: null } : c));
                              }}
                            >
                              {allowEditPhoto ? (
                                <Button icon={<UploadOutlined />}>Upload Photo</Button>
                              ) : null}
                            </Upload>
                            {previewUrl && (
                              <img
                                src={previewUrl}
                                alt={fileList[0]?.name || 'collect'}
                                style={{
                                  width: 140,
                                  height: 140,
                                  objectFit: 'cover',
                                  borderRadius: 6,
                                  border: '1px solid #d9d9d9',
                                  cursor: 'pointer',
                                }}
                                onClick={() => window.open(previewUrl, '_blank')}
                              />
                            )}
                            {fileList.length > 0 && (
                              <Space size={4}>
                                <Button
                                  type="link"
                                  icon={<DownloadOutlined />}
                                  disabled={false}
                                  onClick={() => handleDownloadAttachment('collect', typeof collect.id === 'number' ? collect.id : undefined, fileList[0])}
                                />
                              </Space>
                            )}
                            {allowEditPhoto && canDelete && (
                              <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteCollect(collect.id!)}
                              />
                            )}
                          </div>
                        );
                      })}
                      {collects.length === 0 && (
                        <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                          No collects. Click 'Add' to add a collect.
                        </div>
                      )}
                    </Card>
                  </Col>
                  <Col span={12}>
                    {/* Return */}
                    <Card
                      title="Return"
                      bodyStyle={{ padding: '16px' }}
                      extra={
                        allowEditPhoto ? (
                          <Button
                            type="link"
                            icon={<PlusOutlined />}
                            onClick={handleAddReturn}
                          >
                            Add
                          </Button>
                        ) : null
                      }
                    >
                      {returns.map((returnItem, index) => {
                        const key = getReturnKey(returnItem, index);
                        const file = returnUploads[key];
                        const existingAttachment = returnItem.attachment;
                        const previewUrl = returnPreviewUrls[key];
                        const fileList: UploadFile[] = file
                          ? [{
                              uid: `${key}-upload`,
                              name: file.name,
                              status: 'done',
                              originFileObj: file as any,
                            }]
                          : existingAttachment
                            ? [{
                                uid: `${key}-existing`,
                                name: getAttachmentName(existingAttachment),
                                status: 'done',
                              }]
                            : [];

                        return (
                          <div key={returnItem.id || index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Upload
                              beforeUpload={() => false}
                              maxCount={1}
                              fileList={fileList}
                              disabled={!allowEditPhoto}
                              showUploadList={false}
                              onChange={({ fileList: nextList }) => {
                                if (!allowEditPhoto) return;
                                const nextFile = nextList[0]?.originFileObj as File | undefined;
                                setReturnUploads(prev => ({
                                  ...prev,
                                  [key]: nextFile || null,
                                }));
                                if (nextFile) {
                                  setReturns(prev => prev.map(r => r.id === returnItem.id ? { ...r, attachment: null, name: r.name || nextFile.name } : r));
                                }
                              }}
                              onRemove={() => {
                                if (!allowEditPhoto) return;
                                setReturnUploads(prev => ({ ...prev, [key]: null }));
                                setReturns(prev => prev.map(r => r.id === returnItem.id ? { ...r, attachment: null } : r));
                              }}
                            >
                              {allowEditPhoto ? (
                                <Button icon={<UploadOutlined />}>Upload Photo</Button>
                              ) : null}
                            </Upload>
                            {previewUrl && (
                              <img
                                src={previewUrl}
                                alt={fileList[0]?.name || 'return'}
                                style={{
                                  width: 140,
                                  height: 140,
                                  objectFit: 'cover',
                                  borderRadius: 6,
                                  border: '1px solid #d9d9d9',
                                  cursor: 'pointer',
                                }}
                                onClick={() => window.open(previewUrl, '_blank')}
                              />
                            )}
                            {fileList.length > 0 && (
                              <Space size={4}>
                                <Button
                                  type="link"
                                  icon={<DownloadOutlined />}
                                  disabled={false}
                                  onClick={() => handleDownloadAttachment('return', typeof returnItem.id === 'number' ? returnItem.id : undefined, fileList[0])}
                                />
                              </Space>
                            )}
                            {allowEditPhoto && canDelete && (
                              <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteReturn(returnItem.id!)}
                              />
                            )}
                          </div>
                        );
                      })}
                      {returns.length === 0 && (
                        <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                          No returns. Click 'Add' to add a return.
                        </div>
                      )}
                    </Card>
                  </Col>
                </>
              )}
            </Row>
          </TabPane>
          )}

          {/* Comment Tab */}
          {tabVisibility.find((tab) => tab.key === 'comment')?.visible && (
          <TabPane tab="Comment" key="comment">
            <Card title="Comments" bodyStyle={{ padding: '16px' }}>
              <div style={{ marginBottom: 16 }}>
                {comments.map((comment, index) => (
                  <div
                    key={comment.id || index}
                    style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <div style={{ marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                      <Text>{comment.comment || '-'}</Text>
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      By {comment.user?.name || comment.creator?.name || 'Unknown'} • {formatDateTime(comment.created_at)}
                      {canDelete && (
                        <Button
                          type="link"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteComment(comment.id!)}
                          style={{ marginLeft: 8, padding: 0 }}
                        />
                      )}
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                    No comments yet.
                  </div>
                )}
              </div>
              {allowEditComment && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                  <TextArea
                    rows={6}
                    placeholder="Enter comment..."
                    value={commentText}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCommentText(e.target.value)}
                  />
                  <Button
                    type="primary"
                    onClick={handleAddComment}
                    style={{ marginTop: 12, width: '100%' }}
                    disabled={!commentText.trim()}
                  >
                    Add Comment
                  </Button>
                </div>
              )}
            </Card>
          </TabPane>
          )}

          {/* Review Tab */}
          {tabVisibility.find((tab) => tab.key === 'review')?.visible && (
          <TabPane tab="Review" key="review">
            <Card title="Reviews" bodyStyle={{ padding: '16px' }}>
              {/* Manager Reviews */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '16px' }}>Manager Reviews</div>
                <div style={{ marginBottom: 16 }}>
                  {reviews.filter((review: DispatchReview) => !review.customer_id).map((review, index) => (
                    <div
                      key={review.id || index}
                      style={{
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <Rate disabled value={review.rating || 0} />
                        <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                          <Text>{review.review || '-'}</Text>
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        By {review.creator?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                        {canDelete && (
                          <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteReview(review.id!)}
                            style={{ marginLeft: 8, padding: 0 }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  {reviews.filter((review: DispatchReview) => !review.customer_id).length === 0 && (
                    <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No manager reviews yet.
                    </div>
                  )}
                </div>
                {allowEditReview && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                    <div style={{ marginBottom: 8 }}>
                      <Rate value={reviewRating || 0} onChange={(value) => setReviewRating(value || null)} />
                    </div>
                    <TextArea
                      rows={6}
                      placeholder="Enter review..."
                      value={reviewText}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReviewText(e.target.value)}
                    />
                    <Button
                      type="primary"
                      onClick={handleAddReview}
                      style={{ marginTop: 12, width: '100%' }}
                      disabled={!reviewText.trim() || !reviewRating}
                    >
                      Add Review
                    </Button>
                  </div>
                )}
              </div>

              {/* Customer Reviews */}
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24 }}>
                <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '16px' }}>Customer Reviews</div>
                {reviews.filter((review: DispatchReview) => review.customer_id).length > 0 ? (
                  <div>
                    {reviews.filter((review: DispatchReview) => review.customer_id).map((review: DispatchReview) => (
                      <div
                        key={review.id}
                        style={{
                          padding: '12px 0',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <Rate disabled value={review.rating || 0} />
                          <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                            <Text>{review.review || '-'}</Text>
                          </div>
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          By {review.customer?.name || review.creator?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                    No customer reviews submitted yet
                  </div>
                )}
              </div>
            </Card>
          </TabPane>
          )}

        </Tabs>

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
        }}>
          {isEditing && canVerifyDispatch && (
            <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status} noStyle>
              {({ getFieldValue }) => {
                const rawStatus = getFieldValue('status');
                const normalized = rawStatus
                  ? rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase()
                  : 'Plan';
                const currentStatus = normalized === 'Pending' ? 'Plan' : normalized;
                const isCollectChecked = currentStatus === 'Collect' || currentStatus === 'Return';
                const isReturnChecked = currentStatus === 'Return';
                return (
                  <Space size={12}>
                    <Checkbox
                      checked={isCollectChecked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          form.setFieldsValue({ status: 'Collect' });
                        } else {
                          form.setFieldsValue({ status: 'Plan' });
                        }
                      }}
                    >
                      Collect
                    </Checkbox>
                    <Checkbox
                      checked={isReturnChecked}
                      disabled={!isCollectChecked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          form.setFieldsValue({ status: 'Return' });
                        } else {
                          form.setFieldsValue({ status: 'Collect' });
                        }
                      }}
                    >
                      Return
                    </Checkbox>
                  </Space>
                );
              }}
            </Form.Item>
          )}
          <Space>
            {isEditing && (
              <Button 
                onClick={() => {
                  setIsEditing(false);
                  // Reload dispatch data to reset form
                  if (isEdit && id) {
                    loadDispatch(parseInt(id, 10));
                  }
                }}
                disabled={false}
              >
                Cancel
              </Button>
            )}
            {isEditing && (
              <Button 
                type="primary" 
                onClick={() => {
                  form.submit();
                }} 
                loading={submitting}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            )}
          </Space>
        </div>
      </Form>
      
      {/* Buttons outside Form to avoid disabled state */}
      {!isEditing && isEdit && (
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
        }}>
          <Button onClick={() => navigate('/dispatches')}>Cancel</Button>
        </div>
      )}
    </Card>
  );
};
